// Article Reader Component with Markdown and AI Features
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Star,
  CheckCircle,
  Circle,
  ExternalLink,
  Sparkles,
  Download,
  Tag as TagIcon,
  Loader2,
} from 'lucide-react';
import { type Article, api } from '@/lib/api';
import Button from '@/components/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { formatDateTime } from '@/lib/utils';
import { cn } from '@/lib/utils';

interface ArticleReaderProps {
  article: Article | null | undefined;
}

export default function ArticleReader({ article }: ArticleReaderProps) {
  const queryClient = useQueryClient();
  const [showingSummary, setShowingSummary] = useState(true);

  // Fetch full article details with tags
  const { data: articleData } = useQuery({
    queryKey: ['article', article?.id],
    queryFn: () => article ? api.getArticle(article.id) : null,
    enabled: !!article,
  });

  const fullArticle = articleData?.article || article;
  const tags = articleData?.tags || [];

  // Mutations
  const toggleReadMutation = useMutation({
    mutationFn: (data: { id: number; read: boolean }) =>
      api.updateArticle(data.id, { read: data.read }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['article', article?.id] });
    },
  });

  const toggleStarredMutation = useMutation({
    mutationFn: (data: { id: number; starred: boolean }) =>
      api.updateArticle(data.id, { starred: data.starred }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['article', article?.id] });
    },
  });

  const generateSummaryMutation = useMutation({
    mutationFn: (id: number) => api.summarizeArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article', article?.id] });
    },
  });

  const fetchContentMutation = useMutation({
    mutationFn: (id: number) => api.fetchArticleContent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article', article?.id] });
    },
  });

  if (!article || !fullArticle) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div>
          <Circle className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">Select an article to read</p>
        </div>
      </div>
    );
  }

  const hasFullContent = !!fullArticle.content_markdown;
  const hasSummary = !!fullArticle.summary_ai;

  return (
    <div className="flex h-full flex-col">
      {/* Article Header */}
      <div className="border-b border-border bg-background p-6">
        <h1 className="mb-3 text-2xl font-bold leading-tight">{fullArticle.title}</h1>

        <div className="mb-4 flex items-center gap-4 text-sm text-muted-foreground">
          {fullArticle.author && <span>{fullArticle.author}</span>}
          <span>{formatDateTime(fullArticle.published_at)}</span>
          <a
            href={fullArticle.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            View Original
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={fullArticle.read === 1 ? 'secondary' : 'outline'}
            onClick={() =>
              toggleReadMutation.mutate({
                id: fullArticle.id,
                read: fullArticle.read === 0,
              })
            }
          >
            {fullArticle.read === 1 ? (
              <>
                <CheckCircle className="h-4 w-4" />
                Read
              </>
            ) : (
              <>
                <Circle className="h-4 w-4" />
                Mark as Read
              </>
            )}
          </Button>

          <Button
            size="sm"
            variant={fullArticle.starred === 1 ? 'secondary' : 'outline'}
            onClick={() =>
              toggleStarredMutation.mutate({
                id: fullArticle.id,
                starred: fullArticle.starred === 0,
              })
            }
          >
            <Star
              className={cn(
                'h-4 w-4',
                fullArticle.starred === 1 && 'fill-yellow-400 text-yellow-400'
              )}
            />
            {fullArticle.starred === 1 ? 'Starred' : 'Star'}
          </Button>

          {!hasFullContent && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchContentMutation.mutate(fullArticle.id)}
              disabled={fetchContentMutation.isPending}
            >
              {fetchContentMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Fetching...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Fetch Full Content
                </>
              )}
            </Button>
          )}

          {!hasSummary && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => generateSummaryMutation.mutate(fullArticle.id)}
              disabled={generateSummaryMutation.isPending}
            >
              {generateSummaryMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Generate Summary
                </>
              )}
            </Button>
          )}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <TagIcon className="h-4 w-4 text-muted-foreground" />
            {tags.map((tag: any) => (
              <Badge key={tag.id} variant="secondary">
                {tag.name}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Article Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
        {/* AI Summary */}
        {hasSummary && (
          <Card className="mb-6 border-primary/20 bg-primary/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <CardTitle className="text-base">AI Summary</CardTitle>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowingSummary(!showingSummary)}
                >
                  {showingSummary ? 'Hide' : 'Show'}
                </Button>
              </div>
            </CardHeader>
            {showingSummary && (
              <CardContent>
                <p className="text-sm leading-relaxed">{fullArticle.summary_ai}</p>
              </CardContent>
            )}
          </Card>
        )}

        {/* Article Content */}
        <div className="markdown-content">
          {hasFullContent ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {fullArticle.content_markdown || ''}
            </ReactMarkdown>
          ) : (
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: fullArticle.content_html || '' }}
            />
          )}
        </div>

        {!hasFullContent && !fullArticle.content_html && (
          <div className="py-12 text-center text-muted-foreground">
            <p>No content available</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => fetchContentMutation.mutate(fullArticle.id)}
            >
              <Download className="mr-2 h-4 w-4" />
              Fetch Full Content
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
